import { useContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import './sidebar.scss'
import { Breakpoints, useWindowSize } from '@urbanx/agx-ui-components'
import AutoPilotIcon from './icons/autopilot'
import AutoPilotIconWithText from './icons/autoPilotIconWithText'
import {
  DYNAMIC_MENU_CONFIG,
  filterMenuConfigByRoles,
  getRouteMap,
  MenuItemConfig,
  Pages,
} from 'constants/menu'
import MenuItem from './MenuItem'
import { AuthContext, StaffRole } from '@urbanx/agx.auth.client'
import { StaffAction } from 'types/staff'

interface Props {
  isMenuCollapsed: boolean
  setIsMenuCollapsed: (value: boolean) => void
}

const SideBar = (props: Props) => {
  const { isMenuCollapsed, setIsMenuCollapsed } = props
  const { userAccount } = useContext(AuthContext)
  const { state, pathname } = useLocation()
  const [menuConfig, setMenuConfig] = useState<MenuItemConfig[]>([])
  const [activeLink, setActiveLink] = useState('Tasks')
  const [menuStates, setMenuStates] = useState<Record<string, boolean>>({
    urbanx: false,
    reporting: false,
  })
  const screenSize = useWindowSize()
  const isMobile = screenSize === Breakpoints.Mobile
  const isTabletPortrait = screenSize === Breakpoints.Tablet_Portrait

  const isActiveInSubmenu = (menuId: string) => {
    const menuItem = menuConfig.find((item) => item.id === menuId)
    if (!menuItem?.submenuItems) return false
    return menuItem.submenuItems.some((subItem) => subItem.title === activeLink)
  }

  // Accordion behaviour: opening one dropdown closes the others so the menu
  // never grows beyond the viewport.
  const toggleState = (key: string) => {
    setMenuStates((prev) => {
      const next: Record<string, boolean> = {}
      for (const stateKey of Object.keys(prev)) next[stateKey] = false
      next[key] = !prev[key]
      return next
    })
  }

  const updateActiveLink = (link: string) => {
    setActiveLink(link)
    if (isMobile || isTabletPortrait) {
      setIsMenuCollapsed(true)
    }
  }

  useEffect(() => {
    if (userAccount) {
      setMenuConfig(
        filterMenuConfigByRoles(
          DYNAMIC_MENU_CONFIG,
          userAccount.roles as StaffRole[]
        )
      )
    }
  }, [userAccount])

  useEffect(() => {
    if (pathname === Pages.CREATE_STAFF) {
      setActiveLink('Staff Members')
      setMenuStates({ urbanx: true, reporting: false })
      return
    }

    if (pathname.startsWith(`${Pages.EDIT_STAFF}/`)) {
      const userId = decodeURIComponent(
        pathname.replace(`${Pages.EDIT_STAFF}/`, '')
      )
      const isOwnEditPage =
        state?.action === StaffAction.SELF_EDIT ||
        (userId !== '' && userId === userAccount?.agent)

      setActiveLink(isOwnEditPage ? 'Edit Profile' : 'Staff Members')
      setMenuStates({ urbanx: true, reporting: false })
      return
    }

    const routeConfig = getRouteMap(DYNAMIC_MENU_CONFIG)[pathname]
    if (routeConfig) {
      if (pathname === '/staff' && state?.action === 'view') {
        setActiveLink('Staff Members')
      } else {
        setActiveLink(routeConfig.activeLink)
      }
      if (routeConfig.menuStates) {
        setMenuStates(routeConfig.menuStates)
      }
    }
  }, [pathname, state, userAccount?.agent])

  return (
    <div
      className={`sidebar ${
        isMenuCollapsed ? 'collapsed' : 'sidebar-expanded'
      }`}
      onMouseOver={() => {
        setIsMenuCollapsed(false)
      }}
      onMouseOut={() => {
        setIsMenuCollapsed(true)
      }}
    >
      <nav>
        <ul className="menuShort">
          <li>
            <div className="menu-item">
              <AutoPilotIcon />
            </div>
          </li>
          {DYNAMIC_MENU_CONFIG.filter((item) => !item.isHidden).map((item) => {
            const isActive =
              item.title === activeLink || isActiveInSubmenu(item.id)
            return (
              <li key={item.id}>
                <div className="menu-item">
                  {item.icon && <item.icon opacity={isActive ? 1 : 0.6} />}
                </div>
              </li>
            )
          })}
        </ul>
      </nav>
      <nav>
        <ul className={`menu ${isMenuCollapsed ? 'collapsed' : ''}`}>
          <li>
            <div className="autoPilotIcon">
              <AutoPilotIconWithText />
            </div>
          </li>
          {DYNAMIC_MENU_CONFIG.filter((item) => !item.isHidden).map((item) => {
            const isActive = item.title === activeLink
            const isActiveInSub = isActiveInSubmenu(item.id)

            return (
              <MenuItem
                key={item.id}
                item={item}
                isActive={isActive}
                isMenuCollapsed={isMenuCollapsed}
                menuStates={menuStates}
                onToggleState={toggleState}
                onUpdateActiveLink={updateActiveLink}
                isActiveInSubmenu={isActiveInSub}
                activeLink={activeLink}
              />
            )
          })}
        </ul>
      </nav>
    </div>
  )
}

export default SideBar
